import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { KIND_ORDER, KIND_STYLE } from './constants.js';
import { backlogIssueUrl, normalizeIssueKeyInput } from './ticketImport.js';
import type { BlockKind, SandboxBlock } from './types.js';

/** BlockEditModal の props。 */
export interface BlockEditModalProps {
  /** 編集対象ブロック。null のとき非表示。 */
  block: SandboxBlock | null;
  /** モーダルを閉じる。 */
  onClose: () => void;
  /** 保存（属性の更新）。issueKey が undefined のときは課題キーを外す。 */
  onSave: (
    id: string,
    patch: { name: string; sizePt: number; kind: BlockKind; issueKey: string | undefined },
  ) => void;
}

/**
 * 覆いは `body` の直下へ `createPortal` で出し、層は `--z-modal` で決める。共有シェル
 * (`AppShell`) の本文ラッパーは登場アニメーション後も `transform: translateY(0)` を残し、
 * それが `position: fixed` の包含ブロックになる。木の中に置いたままだと覆いは本文の枠に
 * 閉じ込められ、サイドバー（`--z-sticky`=100）の領域まで届かない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の白板ブロック属性編集モーダル。配置済み/未配置ブロックの種類・サイズ(pt)・名称・Backlog 案件番号をいつでも変更できる。案件番号を入れると Backlog へのリンクが出る
 * @graph-connects normalizeIssueKeyInput [calls] 手入力の案件番号を Backlog 課題キーへ正規化
 */
export function BlockEditModal({ block, onClose, onSave }: BlockEditModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [kind, setKind] = useState<BlockKind>('dev');
  const [issueKeyInput, setIssueKeyInput] = useState('');

  // 対象ブロックが変わるたびフォームを同期する。
  useEffect(() => {
    if (block === null) return;
    setName(block.name);
    setSize(block.sizePt.toString());
    setKind(block.kind);
    setIssueKeyInput(block.issueKey ?? '');
  }, [block]);

  // Esc で閉じる。
  useEffect(() => {
    if (block === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [block, onClose]);

  // 入力中の案件番号の解釈結果。null=未入力 / string=課題キー / undefined=解釈できない。
  const normalizedIssueKey = useMemo(() => normalizeIssueKeyInput(issueKeyInput), [issueKeyInput]);
  const issueKeyInvalid = normalizedIssueKey === undefined;

  const handleSave = useCallback(() => {
    if (block === null) return;
    const trimmed = name.trim();
    const sizePt = Number(size);
    if (trimmed === '' || !Number.isFinite(sizePt) || sizePt <= 0) return;
    if (normalizedIssueKey === undefined) return;
    onSave(block.id, {
      name: trimmed,
      sizePt,
      kind,
      issueKey: normalizedIssueKey ?? undefined,
    });
    onClose();
  }, [block, name, size, kind, normalizedIssueKey, onSave, onClose]);

  if (block === null) return null;

  const sizePtValue = Number(size);
  const canSave =
    name.trim() !== '' && Number.isFinite(sizePtValue) && sizePtValue > 0 && !issueKeyInvalid;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('pjSandbox.edit.title')}
        className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5 shadow-xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
          {t('pjSandbox.edit.title')}
        </h2>

        <div className="flex flex-col gap-4">
          {/* 種類 */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {t('pjSandbox.palette.kind')}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {KIND_ORDER.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setKind(k);
                  }}
                  className="rounded-md border px-3 py-1.5 text-sm transition-colors"
                  style={{
                    borderColor: kind === k ? KIND_STYLE[k].fill : 'var(--color-border)',
                    backgroundColor: kind === k ? KIND_STYLE[k].fill : 'transparent',
                    color: kind === k ? KIND_STYLE[k].text : 'var(--color-text-secondary)',
                  }}
                >
                  {t(KIND_STYLE[k].i18nKey)}
                </button>
              ))}
            </div>
          </div>

          {/* サイズ（任意の数値） */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {t('pjSandbox.palette.size')}
            </span>
            <input
              type="number"
              value={size}
              onChange={(e) => {
                setSize(e.target.value);
              }}
              className="w-32 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
            />
          </label>

          {/* 名称 */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {t('pjSandbox.palette.name')}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
            />
          </label>

          {/* 案件番号（Backlog 課題キー）。入れるとブロックから Backlog へ飛べるようになる。 */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {t('pjSandbox.palette.issueKey')}
            </span>
            <input
              type="text"
              value={issueKeyInput}
              onChange={(e) => {
                setIssueKeyInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              placeholder={t('pjSandbox.edit.issueKeyPlaceholder')}
              aria-label={t('pjSandbox.palette.issueKey')}
              aria-invalid={issueKeyInvalid}
              className="w-full rounded-md border bg-[var(--color-bg-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
              style={{
                borderColor: issueKeyInvalid ? 'var(--color-error)' : 'var(--color-border)',
              }}
            />
            {issueKeyInvalid ? (
              <span className="text-[11px] text-[var(--color-error)]">
                {t('pjSandbox.edit.issueKeyInvalid')}
              </span>
            ) : normalizedIssueKey !== null ? (
              <a
                href={backlogIssueUrl(normalizedIssueKey)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-[var(--color-accent-primary)] underline"
              >
                {t('pjSandbox.edit.openBacklog', { issueKey: normalizedIssueKey })}
              </a>
            ) : (
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {t('pjSandbox.edit.issueKeyHint')}
              </span>
            )}
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)]"
          >
            {t('pjSandbox.edit.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-md bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('pjSandbox.edit.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
